import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Send, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import TopBar from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const PROMPTS = [
  "How do I transcribe a long class?",
  "Help me word a parent update in Hindi",
  "What subjects work best with ClassBridge?",
];

export default function Chatbot() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        return api.get("/chat/history");
      })
      .then((r) => setMessages(r.data))
      .catch(() => navigate("/login"));
  }, [navigate]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  if (!user) return null;

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [
      ...m,
      { role: "user", content: msg, id: `u-${Date.now()}` },
      { role: "assistant", content: "", id: `a-${Date.now()}` },
    ]);
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") break;
          const token = JSON.parse(payload).t;
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + token };
            return copy;
          });
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, something went wrong. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const onSubmit = (e) => { e.preventDefault(); send(); };

  return (
    <main data-testid="chatbot-page" className="flex min-h-screen flex-col bg-[#FDF3EC]">
      <TopBar user={user} />
      <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col px-5 py-8 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2.5"
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-[#E8542A]">
            <MessageSquare className="h-4 w-4 text-white" />
          </div>
          <div>
            <h1 data-testid="chatbot-title" className="font-display text-xl font-bold text-[#1A1918]">
              ClassBridge Assistant
            </h1>
            <p className="text-xs text-[#8A6D5C]">Powered by Claude AI</p>
          </div>
        </motion.div>

        <div className="mt-6 flex-1 space-y-3 pb-4">
          {messages.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="space-y-4"
            >
              <p data-testid="chatbot-empty" className="rounded-2xl border border-dashed border-[#E8542A]/30 bg-white/60 p-8 text-center text-sm text-[#8A6D5C]">
                Ask me anything — lesson ideas, parent update wording, or how to use ClassBridge.
              </p>
              <div className="flex flex-wrap gap-2">
                {PROMPTS.map((p) => (
                  <button
                    key={p}
                    onClick={() => send(p)}
                    className="rounded-full border border-[#E8D5C8] bg-white px-4 py-2 text-xs font-medium text-[#8A6D5C] transition-all hover:border-[#E8542A]/40 hover:text-[#E8542A]"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
          <AnimatePresence initial={false}>
            {messages.map((m, i) => (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                data-testid={`chat-message-${i}`}
                className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "ml-auto rounded-br-sm bg-[#E8542A] text-white"
                    : "rounded-bl-sm border border-black/5 bg-white text-[#1A1918]"
                }`}
              >
                {m.content}
                {m.role === "assistant" && !m.content && streaming && (
                  <span className="inline-flex gap-0.5">
                    {[0, 1, 2].map((n) => (
                      <span
                        key={n}
                        className="inline-block h-1.5 w-1.5 rounded-full bg-[#8A6D5C]"
                        style={{ animation: `waveform 0.8s ${n * 0.15}s ease-in-out infinite alternate` }}
                      />
                    ))}
                  </span>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
          <div ref={bottomRef} />
        </div>

        <form onSubmit={onSubmit} className="flex gap-2.5">
          <Input
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask the assistant..."
            className="rounded-2xl border-[#E8D5C8] bg-white px-5 py-5 focus:border-[#E8542A]"
          />
          <Button
            type="submit"
            data-testid="chat-send-btn"
            disabled={streaming}
            className="rounded-2xl bg-[#E8542A] px-5 text-white hover:bg-[#C94520]"
          >
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </div>
    </main>
  );
}


